import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  queries,
  service,
  useAuthConfig,
  cn,
  parseDate,
  formatDate,
  TimeFormats,
} from "@theprelude/core";
import {
  Button,
  CloseIcon,
  IconButton,
  notifyError,
  RouteIcon,
  UploadIcon,
  notifySuccess,
  FileUploadIcon,
} from "@theprelude/ds";
import { SCMThreat } from "@theprelude/sdk";
import { useEffect, useMemo, useState, type JSX } from "react";
import { DropdownSelect } from "@/components/dropdown-select";
import { FileDropZone } from "@/components/file-drop-zone";
import ThreatImage from "@/components/threat-intel/threat-image";
import { tb } from "@/lib/tracking";
import { useAggregateTechniqueSummary } from "@/queries/control-monitor";
import { DefaultTooltip } from "../default-tooltip";

function getRandomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function ThreatSelector() {
  const authConfig = useAuthConfig();
  const [selectedThreat, setSelectedThreat] = useState<string | undefined>(
    undefined,
  );
  const [showUploadCard, setShowUploadCard] = useState(false);
  const threats = useSuspenseQuery({
    ...queries.scm.threats(authConfig),
    select: (data) =>
      data.toSorted(
        (a, b) =>
          parseDate(b.created).getTime() - parseDate(a.created).getTime(),
      ),
  });

  const { data: aggregateSummary, isLoading } = useAggregateTechniqueSummary({
    threatId: selectedThreat,
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (selectedThreat && aggregateSummary && !isLoading) {
      void navigate({
        to: "/control-monitor",
        search: (prev) => ({
          ...prev,
          technique: undefined,
          threat_id: selectedThreat,
          overlay: undefined,
        }),
      });
    }
  }, [navigate, selectedThreat, aggregateSummary, isLoading]);

  if (showUploadCard) {
    return (
      <UploadThreatCard
        onClose={() => setShowUploadCard(false)}
        onUpload={(threat) => {
          setSelectedThreat(threat.id);
          setShowUploadCard(false);
        }}
      />
    );
  }

  return (
    <div className="flex items-center gap-2 ease-linear">
      <DropdownSelect
        renderOption={({ label, option }) => (
          <div className="flex items-center justify-between gap-1 text-sm">
            <span>{label}</span>
            {option.account_id !== "prelude" && (
              <DefaultTooltip
                content={`Uploaded on ${formatDate(
                  parseDate(option.created),
                  TimeFormats.DATE_FIRST,
                )}`}
              >
                <FileUploadIcon className="size-4 text-secondary-foreground" />
              </DefaultTooltip>
            )}
          </div>
        )}
        options={threats.data}
        placeholder="Search by threat"
        selection={new Set([selectedThreat])}
        getLabel={(t) => t.name}
        getValue={(t) => t.id}
        onChangeSelection={setSelectedThreat}
        loading={isLoading}
        disabled={isLoading}
        icon={<RouteIcon className="text-secondary-foreground" />}
        intent="secondary"
        className={cn("h-[38px]", tb("evaluate"))}
        contentSize="medium"
        align="start"
        searchDelay={100}
      >
        <span className="hidden md:block">Evaluate against a threat</span>
      </DropdownSelect>
      <Button
        icon={<UploadIcon className="text-secondary-foreground" />}
        type="button"
        intent="secondary"
        className={cn("h-[38px]", tb("upload"))}
        disabled={isLoading}
        onClick={() => setShowUploadCard(true)}
      >
        <span className="hidden md:block">Upload a threat</span>
      </Button>
    </div>
  );
}

function UploadThreatCard({
  onClose,
  onUpload,
}: {
  onClose: () => void;
  onUpload: (threat: SCMThreat) => void;
}): JSX.Element {
  const authConfig = useAuthConfig();

  const uploadThreatIntel = useMutation({
    mutationFn: async (file: File) => {
      const { techniques, title } =
        await service(authConfig).scm.generateThreatIntel(file);

      return await service(authConfig).scm.createThreat({
        name: title ?? file.name,
        techniques: techniques?.join(",") ?? "",
      });
    },
    onSuccess: async (threat) => {
      notifySuccess({ message: "Threat uploaded successfully" });
      onUpload(threat);
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to upload the file",
        error,
        dismissible: true,
      });
    },
  });

  const handleFileChange = (file: File | null) => {
    if (!file) return;
    validateAndUploadFile(file);
  };

  const validateAndUploadFile = (file: File) => {
    if (file.type !== "application/pdf") {
      return notifyError({
        message: "File type must be a .pdf",
        dismissible: true,
      });
    }
    if (file.size > 5_000_000) {
      return notifyError({
        message: "File size must be under 5MB",
        dismissible: true,
      });
    }

    uploadThreatIntel.mutate(file);
  };

  const randomNumber = useMemo(() => getRandomInt(1, 4), []);

  if (uploadThreatIntel.isPending) {
    return (
      <Card>
        <div className="flex items-center justify-between border-b border-stroke-line px-3 py-2">
          <h2 className="flex items-center gap-2 font-medium">
            <UploadIcon className="size-4 text-secondary-foreground" /> Upload a
            threat
          </h2>
          <IconButton className="p-0" icon={<CloseIcon />} onClick={onClose} />
        </div>

        <div className="flex flex-col items-center justify-center gap-4 p-6">
          <ThreatImage bgNumber={randomNumber} elNumber={randomNumber} />
          <h3 className="text-lg">Uploading...</h3>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-stroke-line px-3 py-2">
        <h2 className="flex items-center gap-2 font-medium">
          <UploadIcon className="size-4 text-secondary-foreground" /> Upload a
          threat
        </h2>
        <IconButton className="p-0" icon={<CloseIcon />} onClick={onClose} />
      </div>

      <div className="flex flex-col items-center justify-center gap-4 px-20 py-8">
        <h3 className="text-center text-lg">
          Please upload a piece of threat intelligence to begin.
        </h3>

        <FileDropZone
          accept={[".pdf"]}
          disabled={uploadThreatIntel.isPending}
          loading={uploadThreatIntel.isPending}
          onChange={handleFileChange}
        />
      </div>
    </Card>
  );
}

function Card({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex max-h-[500px] w-[500px] max-w-full flex-col border border-stroke-line bg-surface-background backdrop-blur ease-linear",
        className,
      )}
    >
      {children}
    </div>
  );
}
